import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import type { BillingHistory as IBillingHistory } from '@/types/subscriptions';

const BillingHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [billingHistory, setBillingHistory] = useState<IBillingHistory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mock data based on Figma design
  const mockBillingHistory: IBillingHistory = {
    subscription: {
      id: '1',
      user_id: 'user-1',
      plan_id: 'pro-monthly',
      lago_subscription_id: 'lago-sub-1',
      status: 'active',
      is_active: true,
      created_at: '2025-06-15T10:00:00Z',
      updated_at: '2025-06-15T10:00:00Z',
      plan: {
        id: 'pro-monthly',
        name: 'Pro Monthly Plan',
        description: 'Professional monthly plan with enhanced features',
        lago_plan_code: 'pro-monthly-001',
        lago_plan_id: 'lago-plan-1',
        generation_status: 'completed',
        is_active: true,
        amount_cents: 4900,
        currency: 'USD',
        interval: 'monthly',
      },
      service: {
        id: 'weather-api',
        name: 'Live Weather Data Provider',
        description: 'Real-time weather data and forecasting service',
        icon_url: 'https://example.com/weather-icon.png',
        status: 'active',
      },
    },
    records: [
      {
        id: '1',
        date: '2025-06-15',
        description: 'Monthly Renewal',
        amount: 49.00,
        timestamp: '2025-06-18T14:34:00Z',
        status: 'completed',
      },
      {
        id: '2',
        date: '2025-05-15',
        description: 'Monthly Renewal',
        amount: 49.00,
        timestamp: '2025-06-18T09:12:00Z',
        status: 'completed',
      },
      {
        id: '3',
        date: '2025-04-15',
        description: 'Monthly Renewal',
        amount: 49.00,
        timestamp: '2025-06-17T20:01:00Z',
        status: 'completed',
      },
      {
        id: '4',
        date: '2025-03-15',
        description: 'Monthly Renewal',
        amount: 49.00,
        timestamp: '2025-06-17T11:45:00Z',
        status: 'completed',
      },
      {
        id: '5',
        date: '2025-02-15',
        description: 'Monthly Renewal',
        amount: 49.00,
        timestamp: '2025-06-16T17:55:00Z',
        status: 'completed',
      },
    ],
    total_pages: 5,
    current_page: 1,
  };

  useEffect(() => {
    const fetchBillingHistory = async () => {
      try {
        setIsLoading(true);
        // In a real app, this would be an API call with the subscription ID
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (!id) {
          throw new Error('Subscription ID is required');
        }

        setBillingHistory(mockBillingHistory);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load billing history');
        console.error('Error fetching billing history:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBillingHistory();
  }, [id]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTimestamp = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getServiceIcon = (serviceName: string): string => {
    return serviceName.charAt(0).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" text="Loading billing history..." />
      </div>
    );
  }

  if (error || !billingHistory) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600">{error || 'Billing history not found'}</p>
          <Button
            onClick={() => navigate('/my-account/subscriptions')}
            className="mt-4"
          >
            Back to Subscriptions
          </Button>
        </div>
      </div>
    );
  }

  const { subscription, records } = billingHistory;

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold text-gray-900">Billing History</h1>
      </div>

      {/* Service Info Card */}
      <Card className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-4">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-xl"
            style={{ backgroundColor: '#2563EB' }}
          >
            {getServiceIcon(subscription.service.name)}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{subscription.service.name}</h2>
            <p className="text-sm text-gray-600">{subscription.plan.name}</p>
          </div>
        </div>
      </Card>

      {/* Billing History Table */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">History</h3>

        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map((record) => (
                  <tr key={record.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-800">
                      {formatDate(record.date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {record.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(record.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatTimestamp(record.timestamp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BillingHistory;
